import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlarmasLayout } from '../../components/alarmas/AlarmasLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { toast } from 'sonner';
import { Siren, Wifi, WifiOff, RefreshCw, Bell, BellRing, MapPin, ShieldAlert, BellOff, Volume2, Stethoscope } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOUND_OPTIONS = [
    { value: 'clasico', label: 'Clasico' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'sirena', label: 'Sirena' },
    { value: 'suave', label: 'Suave' },
    { value: 'silencio', label: 'Silencioso' },
];

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
}

export default function AlarmasPanelPage() {
    const { user } = useAuth();
    const isAdmin = ['admin', 'superadmin', 'gerente'].includes(user?.rol);
    const [dispositivos, setDispositivos] = useState([]);
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(null);
    const [sosLoading, setSosLoading] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const [userSound, setUserSound] = useState('clasico');
    const [showIOSBanner, setShowIOSBanner] = useState(false);
    const [canTest, setCanTest] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [longPressMs, setLongPressMs] = useState(800);
    const [sosLongPressMs, setSosLongPressMs] = useState(1200);
    const [pressProgress, setPressProgress] = useState({}); // { [devId]: 0-100 }
    const [sosProgress, setSosProgress] = useState(0);
    const [dialogDireccion, setDialogDireccion] = useState(false);
    const [direccionInput, setDireccionInput] = useState('');
    const [diagStep, setDiagStep] = useState('idle'); // idle | waiting | check | results
    const [diagAnswers, setDiagAnswers] = useState({ visto: null, sonido: null, vibro: null, bloqueo: null });
    const [diagSending, setDiagSending] = useState(false);

    const cooldownRef = useRef(0);
    const prevNotifCountRef = useRef(0);
    const pressTimersRef = useRef({}); // { [devId]: { startAt, interval, triggered } }
    const sosTimerRef = useRef(null);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        if (isIOS && !isStandalone) setShowIOSBanner(true);
        loadPreferences();
        loadPublicConfig();
        checkDireccion();
    }, []);

    const loadPublicConfig = async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/public-config`);
            if (res.ok) {
                const d = await res.json();
                if (d.long_press_ms) setLongPressMs(d.long_press_ms);
                if (d.sos_long_press_ms) setSosLongPressMs(d.sos_long_press_ms);
            }
        } catch { /* ignore */ }
    };

    const checkDireccion = async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/me/profile`, { headers });
            if (res.ok) {
                const me = await res.json();
                const esAlarmasUser = Array.isArray(me.modulos_acceso) && me.modulos_acceso.includes('alarmas_ls');
                const esAdmin = ['admin', 'superadmin', 'gerente'].includes(me.rol);
                if (esAlarmasUser && !esAdmin && !me.direccion) {
                    setDialogDireccion(true);
                }
            }
        } catch { /* ignore */ }
    };

    const handleSaveDireccion = async () => {
        const val = direccionInput.trim();
        if (val.length < 3) {
            toast.error('Ingresa tu calle y número');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/alarmas/me/direccion`, {
                method: 'PUT', headers,
                body: JSON.stringify({ direccion: val }),
            });
            if (res.ok) {
                toast.success('Dirección guardada');
                setDialogDireccion(false);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error de conexión'); }
    };

    const loadPreferences = async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/preferencias`, { headers });
            if (res.ok) {
                const data = await res.json();
                setUserSound(data.sonido || 'clasico');
                setCanTest(!!data.puede_probar);
            }
        } catch { /* ignore */ }
    };

    const saveSound = async (sound) => {
        setUserSound(sound);
        playAlertSound(sound);
        try {
            await fetch(`${API_URL}/api/alarmas/preferencias`, {
                method: 'PUT', headers,
                body: JSON.stringify({ sonido: sound }),
            });
        } catch { /* ignore */ }
    };

    const getGPS = () => new Promise((resolve) => {
        if (!navigator.geolocation) return resolve({ lat: null, lng: null });
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve({ lat: null, lng: null }),
            { timeout: 5000, enableHighAccuracy: true }
        );
    });

    // Carga solo notificaciones (ligero, sin Tuya)
    const loadNotifications = useCallback(async () => {
        try {
            const nRes = await fetch(`${API_URL}/api/alarmas/notificaciones`, { headers });
            if (nRes.ok) setNotificaciones(await nRes.json());
        } catch { /* silencioso */ }
    }, []);

    // Carga dispositivos con estado Tuya
    const loadDevices = useCallback(async () => {
        // Si estamos en cooldown, no consultar Tuya
        if (Date.now() < cooldownRef.current) return;
        try {
            const dRes = await fetch(`${API_URL}/api/alarmas/mis-dispositivos`, { headers });
            if (dRes.ok) {
                const data = await dRes.json();
                // Solo actualizar si NO estamos en cooldown (doble check)
                if (Date.now() >= cooldownRef.current) {
                    setDispositivos(data);
                }
            }
        } catch { /* silencioso */ }
    }, []);

    const loadAll = useCallback(async () => {
        await Promise.all([loadDevices(), loadNotifications()]);
        setLoading(false);
    }, [loadDevices, loadNotifications]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Auto-refresh: notificaciones cada 5s, dispositivos cada 15s
    useEffect(() => {
        const notifInterval = setInterval(async () => {
            try {
                const nRes = await fetch(`${API_URL}/api/alarmas/notificaciones`, { headers });
                if (nRes.ok) {
                    const newNotifs = await nRes.json();
                    if (newNotifs.length > prevNotifCountRef.current && prevNotifCountRef.current >= 0) {
                        playAlertSound(userSound);
                        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
                    }
                    prevNotifCountRef.current = newNotifs.length;
                    setNotificaciones(newNotifs);
                }
            } catch { /* ignore */ }
        }, 5000);
        const deviceInterval = setInterval(loadDevices, 15000);
        return () => { clearInterval(notifInterval); clearInterval(deviceInterval); };
    }, [loadDevices, userSound]);

    const playAlertSound = (sound) => {
        try {
            const file = sound === 'silencio' ? null : `/sounds/alert-${sound || 'clasico'}.wav`;
            if (!file) return;
            const audio = new Audio(file);
            audio.volume = 0.8;
            audio.play().catch(() => {});
        } catch { /* ignore */ }
    };

    // Push subscription - pedir permiso explícitamente
    useEffect(() => { checkPushStatus(); }, []);

    const checkPushStatus = async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setPushEnabled(!!sub);
        } catch { /* ignore */ }
    };

    const requestPushPermission = async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            // Detectar iOS
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
            if (isIOS && !isStandalone) {
                toast.error('En iPhone: primero instale la app. Toque el icono de compartir en Safari y luego "Agregar a inicio"', { duration: 8000 });
                return;
            }
            toast.error('Este navegador no soporta notificaciones push');
            return;
        }
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                toast.error('Permiso de notificaciones denegado. Active las notificaciones en la configuracion del navegador.');
                return;
            }

            const reg = await navigator.serviceWorker.ready;

            // Eliminar suscripción anterior si existe
            const existingSub = await reg.pushManager.getSubscription();
            if (existingSub) await existingSub.unsubscribe();

            // Obtener VAPID key
            const res = await fetch(`${API_URL}/api/alarmas/vapid-public-key`, { headers });
            if (!res.ok) { toast.error('Error al obtener clave'); return; }
            const { key } = await res.json();
            if (!key) { toast.error('Clave VAPID no configurada'); return; }

            const newSub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
            });

            // Guardar en servidor
            const saveRes = await fetch(`${API_URL}/api/alarmas/push/subscribe`, {
                method: 'POST', headers,
                body: JSON.stringify({ subscription: newSub.toJSON() }),
            });

            if (saveRes.ok) {
                setPushEnabled(true);
                toast.success('Notificaciones push activadas');
            } else {
                toast.error('Error al guardar suscripcion');
            }
        } catch (e) {
            console.error('Push error:', e);
            toast.error('Error al configurar notificaciones');
        }
    };

    const startLongPress = (disp) => {
        if (toggling || !disp.online) return;
        // Evitar re-disparo si ya está procesando esta tecla
        if (pressTimersRef.current[disp.id]?.interval) return;
        const startAt = Date.now();
        pressTimersRef.current[disp.id] = { startAt, triggered: false };
        // Vibración ligera al iniciar
        if (navigator.vibrate) navigator.vibrate(15);
        const interval = setInterval(() => {
            const elapsed = Date.now() - startAt;
            const progress = Math.min(100, (elapsed / longPressMs) * 100);
            setPressProgress(prev => ({ ...prev, [disp.id]: progress }));
            if (progress >= 100 && !pressTimersRef.current[disp.id]?.triggered) {
                pressTimersRef.current[disp.id].triggered = true;
                cancelLongPress(disp.id);
                if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
                handleToggle(disp);
            }
        }, 30);
        pressTimersRef.current[disp.id].interval = interval;
    };

    const cancelLongPress = (devId) => {
        const t = pressTimersRef.current[devId];
        if (t?.interval) clearInterval(t.interval);
        pressTimersRef.current[devId] = null;
        setPressProgress(prev => ({ ...prev, [devId]: 0 }));
    };

    const handleToggle = async (disp) => {
        if (toggling) return; // Prevenir doble click
        const newAction = disp.switch ? 'off' : 'on';
        setToggling(disp.id);

        // Actualizar UI inmediatamente
        setDispositivos(prev => prev.map(d =>
            d.id === disp.id ? { ...d, switch: newAction === 'on' } : d
        ));

        // Cooldown: ignorar Tuya refresh por 15 segundos
        cooldownRef.current = Date.now() + 15000;

        try {
            const coords = await getGPS();
            const res = await fetch(`${API_URL}/api/alarmas/mis-dispositivos/${disp.id}/toggle`, {
                method: 'POST', headers,
                body: JSON.stringify({ action: newAction, ...coords }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
                // Revertir UI si falló
                setDispositivos(prev => prev.map(d =>
                    d.id === disp.id ? { ...d, switch: !( newAction === 'on') } : d
                ));
                cooldownRef.current = 0;
            }
        } catch {
            toast.error('Error de conexion');
            // Revertir
            setDispositivos(prev => prev.map(d =>
                d.id === disp.id ? { ...d, switch: !(newAction === 'on') } : d
            ));
            cooldownRef.current = 0;
        } finally {
            setToggling(null);
        }
    };

    const handleSOS = async () => {
        setSosLoading(true);
        // Vibrar el dispositivo del que envía
        if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]);
        try {
            const coords = await getGPS();
            const res = await fetch(`${API_URL}/api/alarmas/sos`, {
                method: 'POST', headers,
                body: JSON.stringify(coords),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`SOS enviado a ${data.notificados} vecino(s)`);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch {
            toast.error('Error al enviar SOS');
        } finally {
            setSosLoading(false);
        }
    };

    const startSosLongPress = () => {
        if (sosLoading || sosTimerRef.current) return;
        const startAt = Date.now();
        sosTimerRef.current = { startAt, triggered: false };
        if (navigator.vibrate) navigator.vibrate(20);
        const interval = setInterval(() => {
            const elapsed = Date.now() - startAt;
            const progress = Math.min(100, (elapsed / sosLongPressMs) * 100);
            setSosProgress(progress);
            if (progress >= 100 && !sosTimerRef.current?.triggered) {
                sosTimerRef.current.triggered = true;
                cancelSosLongPress();
                if (navigator.vibrate) navigator.vibrate([150, 80, 150, 80, 150]);
                handleSOS();
            }
        }, 30);
        sosTimerRef.current.interval = interval;
    };

    const cancelSosLongPress = () => {
        if (sosTimerRef.current?.interval) clearInterval(sosTimerRef.current.interval);
        sosTimerRef.current = null;
        setSosProgress(0);
    };

    const handleTest = async () => {
        setTestLoading(true);
        try {
            const coords = await getGPS();
            const res = await fetch(`${API_URL}/api/alarmas/test-alerta`, {
                method: 'POST', headers,
                body: JSON.stringify(coords),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch {
            toast.error('Error');
        } finally {
            setTestLoading(false);
        }
    };


    const handleMarkRead = async () => {
        await fetch(`${API_URL}/api/alarmas/notificaciones/marcar-leidas`, { method: 'POST', headers });
        setNotificaciones([]);
    };

    const startDiagnostico = async () => {
        if (!pushEnabled) {
            toast.error('Primero activa las notificaciones push (botón "Activar")');
            return;
        }
        setDiagStep('waiting');
        setDiagAnswers({ visto: null, sonido: null, vibro: null, bloqueo: null });
        setDiagSending(true);
        try {
            const res = await fetch(`${API_URL}/api/alarmas/diagnostico-push`, {
                method: 'POST', headers,
            });
            if (!res.ok) {
                const err = await res.json();
                toast.error(err.detail || 'Error');
                setDiagStep('idle');
                return;
            }
            toast.success('Enviada. Revisa tu teléfono.');
            // Esperar 5s antes de mostrar preguntas
            setTimeout(() => setDiagStep('check'), 5000);
        } catch {
            toast.error('Error de conexión');
            setDiagStep('idle');
        } finally {
            setDiagSending(false);
        }
    };

    const [notifSheetOpen, setNotifSheetOpen] = useState(false);

    return (
        <AlarmasLayout>
            <div className="space-y-5 animate-fade-in pb-24 md:pb-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="font-black text-2xl md:text-3xl uppercase tracking-tight flex items-center gap-3" data-testid="alarmas-panel-title">
                        <Siren className="h-7 w-7 text-red-500" />
                        Mis Dispositivos
                    </h1>
                    <div className="flex items-center gap-2">
                        {/* Bell con badge -> abre Sheet */}
                        <Sheet open={notifSheetOpen} onOpenChange={setNotifSheetOpen}>
                            <SheetTrigger asChild>
                                <Button variant="outline" size="icon" className="relative" data-testid="btn-open-notifications">
                                    <Bell className="h-4 w-4" />
                                    {notificaciones.length > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse">
                                            {notificaciones.length > 99 ? '99+' : notificaciones.length}
                                        </span>
                                    )}
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                                <SheetHeader>
                                    <SheetTitle className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2">
                                            <Bell className="h-5 w-5 text-amber-500" />
                                            Notificaciones ({notificaciones.length})
                                        </span>
                                        {notificaciones.length > 0 && (
                                            <Button variant="ghost" size="sm" onClick={handleMarkRead} data-testid="btn-clear-notifications">Limpiar</Button>
                                        )}
                                    </SheetTitle>
                                </SheetHeader>
                                <div className="mt-4 space-y-2">
                                    {notificaciones.length === 0 ? (
                                        <div className="text-center py-12 text-muted-foreground">
                                            <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                            <p className="text-sm">Sin notificaciones nuevas</p>
                                        </div>
                                    ) : (
                                        notificaciones.map(n => (
                                            <button
                                                key={n.id}
                                                className={`w-full text-left p-3 rounded-lg text-sm cursor-pointer hover:bg-muted transition-colors border ${n.tipo === 'sos' ? 'bg-red-500/10 border-red-500/30' : 'border-border'}`}
                                                onClick={() => { setSelectedNotif(n); setNotifSheetOpen(false); }}
                                                data-testid={`notif-item-${n.id}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    {n.tipo === 'sos' && <ShieldAlert className="h-4 w-4 text-red-500" />}
                                                    <span className="font-bold">{n.titulo}</span>
                                                    {n.lat && <MapPin className="h-3 w-3 text-muted-foreground ml-auto" />}
                                                </div>
                                                <p className="text-muted-foreground text-xs">{n.mensaje}</p>
                                                {n.created_at && (
                                                    <p className="text-[10px] text-muted-foreground mt-1">
                                                        {new Date(n.created_at).toLocaleString()}
                                                    </p>
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </SheetContent>
                        </Sheet>
                        {pushEnabled ? (
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500"><BellRing className="h-3 w-3 mr-1" />Push</Badge>
                        ) : (
                            <Button variant="outline" size="sm" onClick={requestPushPermission} data-testid="btn-enable-push">
                                <BellOff className="h-4 w-4 mr-1" /> Activar
                            </Button>
                        )}
                        <Button variant="outline" size="icon" onClick={loadAll} data-testid="btn-refresh">
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* iOS Install Banner */}
                {showIOSBanner && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm space-y-1">
                        <p className="font-bold text-blue-600">Para recibir notificaciones en iPhone:</p>
                        <p className="text-muted-foreground">1. Toque el icono <strong>Compartir</strong> en Safari (cuadrado con flecha)</p>
                        <p className="text-muted-foreground">2. Seleccione <strong>"Agregar a pantalla de inicio"</strong></p>
                        <p className="text-muted-foreground">3. Abra la app desde el icono en su pantalla</p>
                        <p className="text-muted-foreground">4. Toque <strong>"Activar Push"</strong></p>
                        <Button variant="ghost" size="sm" onClick={() => setShowIOSBanner(false)} className="mt-1">Entendido</Button>
                    </div>
                )}

                {/* Dispositivos - PRIORITARIO, SIEMPRE VISIBLE ARRIBA */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-48" />)}
                    </div>
                ) : dispositivos.length === 0 ? (
                    <Card className="border-2">
                        <CardContent className="py-16 text-center">
                            <Siren className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                            <p className="text-xl font-bold">Sin dispositivos</p>
                            <p className="text-muted-foreground">No tiene dispositivos asignados</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {dispositivos.map(disp => {
                            const isOn = disp.switch === true;
                            const isProcessing = toggling === disp.id;
                            const progress = pressProgress[disp.id] || 0;
                            const holding = progress > 0 && progress < 100;
                            return (
                                <div
                                    key={disp.id}
                                    role="button"
                                    tabIndex={0}
                                    onMouseDown={() => startLongPress(disp)}
                                    onMouseUp={() => cancelLongPress(disp.id)}
                                    onMouseLeave={() => cancelLongPress(disp.id)}
                                    onTouchStart={() => startLongPress(disp)}
                                    onTouchEnd={() => cancelLongPress(disp.id)}
                                    onTouchCancel={() => cancelLongPress(disp.id)}
                                    onContextMenu={(e) => e.preventDefault()}
                                    style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
                                    aria-disabled={isProcessing || !disp.online}
                                    className={`relative overflow-hidden w-full text-left rounded-2xl border-2 p-6 transition-all select-none cursor-pointer ${holding ? 'scale-[0.98]' : 'active:scale-[0.97]'} ${(isProcessing || !disp.online) ? 'opacity-50 pointer-events-none' : ''} ${
                                        isOn
                                            ? 'border-red-500 bg-red-500 text-white shadow-xl shadow-red-500/30'
                                            : 'border-zinc-300 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-900 hover:border-zinc-400 dark:hover:border-zinc-600'
                                    }`}
                                    data-testid={`device-btn-${disp.id}`}
                                >
                                    {/* Barra de progreso del long-press */}
                                    {holding && (
                                        <div
                                            className="absolute bottom-0 left-0 h-2 bg-red-400 transition-none"
                                            style={{ width: `${progress}%` }}
                                        />
                                    )}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-1.5">
                                            {disp.online ? (
                                                <Wifi className={`h-4 w-4 ${isOn ? 'text-white/70' : 'text-emerald-500'}`} />
                                            ) : (
                                                <WifiOff className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <span className={`text-xs font-medium ${isOn ? 'text-white/70' : 'text-muted-foreground'}`}>
                                                {disp.online ? 'Online' : 'Offline'}
                                            </span>
                                        </div>
                                        <span className={`text-xs font-black uppercase px-2 py-0.5 rounded-full ${
                                            isOn ? 'bg-white/20 text-white' : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                                        }`}>
                                            {isOn ? 'Encendido' : 'Apagado'}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-center gap-3 py-4 relative">
                                        {/* Anillo de progreso circular */}
                                        {holding && (
                                            <svg className="absolute top-0 left-1/2 -translate-x-1/2" width="120" height="120" viewBox="0 0 120 120">
                                                <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="4" className="opacity-20" />
                                                <circle
                                                    cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="4"
                                                    strokeDasharray={`${2 * Math.PI * 52}`}
                                                    strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                                                    strokeLinecap="round"
                                                    transform="rotate(-90 60 60)"
                                                    className={isOn ? 'text-white' : 'text-red-500'}
                                                />
                                            </svg>
                                        )}
                                        <div className={`p-4 rounded-full ${isOn ? 'bg-white/20' : 'bg-zinc-200 dark:bg-zinc-800'} ${holding ? 'scale-110' : ''} transition-transform`}>
                                            <Siren className={`h-10 w-10 ${isOn ? 'text-white animate-pulse' : 'text-zinc-500 dark:text-zinc-400'}`} />
                                        </div>
                                        <span className="font-black text-lg uppercase tracking-wide">{disp.nombre}</span>
                                    </div>
                                    <div className={`text-center text-sm font-bold mt-2 ${isOn ? 'text-white/80' : 'text-zinc-600 dark:text-zinc-400'}`}>
                                        {isProcessing
                                            ? 'Procesando...'
                                            : holding
                                                ? 'Mantén presionado...'
                                                : isOn
                                                    ? 'Mantén presionado para APAGAR'
                                                    : 'Mantén presionado para ENCENDER'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* SOS Button con long-press */}
                <div
                    role="button"
                    tabIndex={0}
                    onMouseDown={startSosLongPress}
                    onMouseUp={cancelSosLongPress}
                    onMouseLeave={cancelSosLongPress}
                    onTouchStart={startSosLongPress}
                    onTouchEnd={cancelSosLongPress}
                    onTouchCancel={cancelSosLongPress}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
                    className={`relative overflow-hidden w-full p-5 rounded-2xl font-black text-lg uppercase tracking-wider flex items-center justify-center gap-3 transition-all select-none cursor-pointer ${sosProgress > 0 && sosProgress < 100 ? 'scale-[0.98]' : 'active:scale-95'} bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg shadow-red-500/30 hover:shadow-red-500/50 ${sosLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid="btn-sos"
                >
                    {/* Barra de progreso del long-press SOS */}
                    {sosProgress > 0 && sosProgress < 100 && (
                        <div
                            className="absolute bottom-0 left-0 h-2 bg-white/80 transition-none"
                            style={{ width: `${sosProgress}%` }}
                        />
                    )}
                    <ShieldAlert className={`h-7 w-7 ${sosLoading ? 'animate-pulse' : sosProgress > 0 ? 'animate-pulse' : ''}`} />
                    {sosLoading
                        ? 'Enviando SOS...'
                        : sosProgress > 0 && sosProgress < 100
                            ? 'Mantén presionado...'
                            : 'SOS Silencioso'}
                </div>

                {/* Test Alert Button */}
                {(canTest || isAdmin) && (
                    <button
                        onClick={handleTest}
                        disabled={testLoading}
                        className="w-full p-3 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95 border-2 border-blue-500/30 bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 disabled:opacity-50"
                        data-testid="btn-test"
                    >
                        <Bell className={`h-5 w-5 ${testLoading ? 'animate-pulse' : ''}`} />
                        {testLoading ? 'Enviando prueba...' : 'Probar Alerta (solo yo)'}
                    </button>
                )}

                {/* Diagnóstico de notificaciones */}
                <button
                    onClick={startDiagnostico}
                    disabled={diagSending || diagStep !== 'idle'}
                    className="w-full p-3 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95 border-2 border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
                    data-testid="btn-diagnostico"
                >
                    <Stethoscope className={`h-5 w-5 ${diagStep === 'waiting' ? 'animate-pulse' : ''}`} />
                    {diagStep === 'waiting' ? 'Enviando prueba...' : 'Diagnóstico de Notificaciones'}
                </button>

                {/* Sonido - al final, compacto */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <Volume2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Sonido:</span>
                    <Select value={userSound} onValueChange={saveSound}>
                        <SelectTrigger className="w-36 h-8" data-testid="select-sound">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SOUND_OPTIONS.map(s => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Dialog: Diagnóstico de Notificaciones */}
            <Dialog open={diagStep === 'waiting' || diagStep === 'check' || diagStep === 'results'} onOpenChange={(v) => !v && setDiagStep('idle')}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-500" /> Diagnóstico</DialogTitle>
                    </DialogHeader>
                    {diagStep === 'waiting' && (
                        <div className="py-6 text-center space-y-3">
                            <div className="w-16 h-16 mx-auto rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
                            <p className="text-base">Enviando notificación de prueba a este dispositivo...</p>
                            <p className="text-sm text-muted-foreground">Minimiza la app si es necesario para ver si llega.</p>
                        </div>
                    )}
                    {diagStep === 'check' && (
                        <div className="py-2 space-y-4">
                            <p className="text-base">Responde según lo que haya pasado con la notificación de prueba:</p>
                            {[
                                { key: 'visto', label: '¿Viste la notificación aparecer?' },
                                { key: 'sonido', label: '¿Sonó el teléfono?' },
                                { key: 'vibro', label: '¿Vibró el teléfono?' },
                                { key: 'bloqueo', label: '¿La viste aunque el teléfono estuviera bloqueado?' },
                            ].map(q => (
                                <div key={q.key} className="flex items-center justify-between gap-3">
                                    <span className="text-base flex-1">{q.label}</span>
                                    <div className="flex gap-1">
                                        <Button
                                            size="sm"
                                            variant={diagAnswers[q.key] === true ? 'default' : 'outline'}
                                            className={diagAnswers[q.key] === true ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                                            onClick={() => setDiagAnswers(p => ({ ...p, [q.key]: true }))}
                                            data-testid={`diag-${q.key}-si`}
                                        >Sí</Button>
                                        <Button
                                            size="sm"
                                            variant={diagAnswers[q.key] === false ? 'default' : 'outline'}
                                            className={diagAnswers[q.key] === false ? 'bg-red-500 hover:bg-red-600' : ''}
                                            onClick={() => setDiagAnswers(p => ({ ...p, [q.key]: false }))}
                                            data-testid={`diag-${q.key}-no`}
                                        >No</Button>
                                    </div>
                                </div>
                            ))}
                            <Button
                                onClick={async () => {
                                    // Persistir en backend antes de mostrar resultado
                                    try {
                                        await fetch(`${API_URL}/api/alarmas/diagnostico-resultado`, {
                                            method: 'POST', headers,
                                            body: JSON.stringify(diagAnswers),
                                        });
                                    } catch { /* ignore */ }
                                    setDiagStep('results');
                                }}
                                disabled={Object.values(diagAnswers).some(v => v === null)}
                                className="w-full mt-4"
                                data-testid="btn-diag-resultado"
                            >
                                Ver resultado
                            </Button>
                        </div>
                    )}
                    {diagStep === 'results' && (
                        <div className="py-2 space-y-3">
                            {diagAnswers.visto && diagAnswers.sonido && diagAnswers.vibro && diagAnswers.bloqueo ? (
                                <div className="p-4 rounded-xl bg-emerald-500/10 border-2 border-emerald-500/30">
                                    <p className="text-base font-bold text-emerald-700 dark:text-emerald-400">¡Todo correcto!</p>
                                    <p className="text-sm mt-1">Tu teléfono está configurado para recibir alertas en cualquier situación.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-base font-bold">Hay ajustes que revisar:</p>
                                    {!diagAnswers.visto && (
                                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm">
                                            <strong>No viste la notificación.</strong> Ve a <strong>Ajustes → Apps → Alarmas LS → Notificaciones</strong> y activa <em>"Mostrar notificaciones"</em> y <em>"Notificaciones flotantes"</em>. También desactiva el <strong>ahorro de batería</strong> para esta app.
                                        </div>
                                    )}
                                    {!diagAnswers.sonido && diagAnswers.visto && (
                                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                                            <strong>No sonó.</strong> En los ajustes de notificaciones de la app activa <em>"Permitir sonido de reproducción"</em>. Revisa también el volumen de notificaciones del teléfono.
                                        </div>
                                    )}
                                    {!diagAnswers.vibro && diagAnswers.visto && (
                                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                                            <strong>No vibró.</strong> Activa <em>"Permitir vibración"</em> en los ajustes de notificaciones de la app.
                                        </div>
                                    )}
                                    {!diagAnswers.bloqueo && diagAnswers.visto && (
                                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                                            <strong>No se ve en pantalla bloqueada.</strong> Activa <em>"Permitir notificaciones en la Pantalla de bloqueo"</em>. Esto es crítico para emergencias nocturnas.
                                        </div>
                                    )}
                                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs">
                                        Después de cambiar los ajustes, vuelve aquí y toca "Diagnóstico" de nuevo para verificar.
                                    </div>
                                </>
                            )}
                            <Button onClick={() => setDiagStep('idle')} className="w-full" variant="outline">Cerrar</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>


            {/* Dialog: Dirección obligatoria */}
            <Dialog open={dialogDireccion} onOpenChange={() => {}}>
                <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <MapPin className="h-5 w-5 text-red-500" />
                            Ingresa tu dirección
                        </DialogTitle>
                        <DialogDescription className="text-base pt-2">
                            Necesitamos tu <strong>calle y número de casa</strong> para que los vecinos
                            sepan desde dónde llegan las alertas.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-2">
                        <Label className="text-base">Calle y número</Label>
                        <Input
                            value={direccionInput}
                            onChange={e => setDireccionInput(e.target.value)}
                            placeholder="Ej: Murillo 1467"
                            className="h-12 text-base"
                            data-testid="input-direccion"
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                            Aparecerá en las alertas: "<strong>{user?.nombre || 'Tu nombre'} de {direccionInput || '...'}</strong> activó ..."
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleSaveDireccion} className="w-full h-12 text-base" data-testid="btn-guardar-direccion">
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Notificación con ubicación */}
            <Dialog open={!!selectedNotif} onOpenChange={(v) => !v && setSelectedNotif(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedNotif?.tipo === 'sos' && <ShieldAlert className="h-5 w-5 text-red-500" />}
                            {selectedNotif?.titulo}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-lg">{selectedNotif?.mensaje}</p>
                        <p className="text-xs text-muted-foreground">
                            {selectedNotif?.created_at && new Date(selectedNotif.created_at).toLocaleString()}
                        </p>
                        {selectedNotif?.lat && selectedNotif?.lng ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <MapPin className="h-4 w-4" />
                                    <span>{Number(selectedNotif.lat).toFixed(6)}, {Number(selectedNotif.lng).toFixed(6)}</span>
                                </div>
                                <a
                                    href={`https://www.google.com/maps?q=${selectedNotif.lat},${selectedNotif.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full p-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600 transition-colors"
                                >
                                    <MapPin className="h-5 w-5" />
                                    Ver Ubicacion en Google Maps
                                </a>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Ubicacion no disponible</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedNotif(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AlarmasLayout>
    );
}
